import apiService from './api';
import type {
  LoginResponse, RegisterResponse, User, UserProfile,
  InvitationStats, InvitationCodeItem, CurrentUserResponse,
  LevelConfigResponse, LevelStatusResponse, FeatureAccessResponse,
  XPHistoryResponse,
} from '../types/api';

export const authService = {
  async login(username: string, password: string): Promise<LoginResponse> {
    return apiService.post<LoginResponse>('/auth/login/', {
      username,
      password,
    });
  },

  async register(data: {
    username: string;
    email: string;
    password: string;
    confirm_password: string;
    invite_code?: string;
    age?: string;
    first_name?: string;
    last_name?: string;
  }): Promise<RegisterResponse> {
    // 转换字段名以匹配后端API期望的格式
    const apiData = {
      username: data.username,
      email: data.email,
      password: data.password,
      password_confirm: data.confirm_password, // 后端期望password_confirm
      invitation_code: data.invite_code || '', // 后端期望invitation_code
      age: data.age || '15+', // 默认年龄
      first_name: data.first_name || '',
      last_name: data.last_name || '',
    };

    return await apiService.post<RegisterResponse>('/auth/register/', apiData);
  },

  async logout(): Promise<void> {
    return apiService.post<void>('/auth/logout/', {});
  },

  async getCurrentUser(): Promise<CurrentUserResponse> {
    return apiService.get<CurrentUserResponse>('/auth/user/');
  },

  async updateProfile(data: Partial<UserProfile>): Promise<{ message: string; profile: UserProfile }> {
    return apiService.put<{ message: string; profile: UserProfile }>('/auth/profile/update/', data);
  },

  async getInvitationStats(): Promise<InvitationStats> {
    const res = await apiService.get<{ stats: InvitationStats }>('/auth/invitation-stats/');
    return res.stats;
  },

  async generateInvitationCode(): Promise<{ code: string; expires_at: string }> {
    const res = await apiService.post<{ invitation: { code: string; expires_at: string } }>('/auth/invitation/generate/', {});
    return res.invitation;
  },

  async getMyInvitations(): Promise<InvitationCodeItem[]> {
    const res = await apiService.get<{ invitations: InvitationCodeItem[] }>('/auth/invitations/');
    return res.invitations;
  },

  async verifyEmail(uidb64: string, token: string): Promise<{ message: string; success: boolean }> {
    return apiService.get<{ message: string; success: boolean }>(`/auth/verify-email/${uidb64}/${token}/`);
  },

  async resendVerification(email: string): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/auth/resend-verification/', { email });
  },

  async requestPasswordReset(username: string, email: string): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/auth/password-reset/', { username, email });
  },

  async confirmPasswordReset(uidb64: string, token: string, newPassword: string): Promise<{ message: string; success: boolean }> {
    return apiService.post<{ message: string; success: boolean }>(`/auth/password-reset/${uidb64}/${token}/`, {
      new_password: newPassword,
    });
  },

  async checkUsernameAvailable(username: string): Promise<{ available: boolean; message: string }> {
    return apiService.get<{ available: boolean; message: string }>(`/auth/check-username/?username=${encodeURIComponent(username)}`);
  },

  // ========== Level System ==========

  async getLevelConfigs(): Promise<LevelConfigResponse> {
    return apiService.get<LevelConfigResponse>('/auth/levels/configs/');
  },

  async getMyLevelStatus(): Promise<LevelStatusResponse> {
    return apiService.get<LevelStatusResponse>('/auth/levels/my-status/');
  },

  async getMyFeatureAccess(): Promise<FeatureAccessResponse> {
    return apiService.get<FeatureAccessResponse>('/auth/levels/feature-access/');
  },

  async getMyXPHistory(limit?: number): Promise<XPHistoryResponse> {
    const query = limit ? `?limit=${limit}` : '';
    return apiService.get<XPHistoryResponse>(`/auth/levels/xp-history/${query}`);
  },

  // ========== QQ OAuth ==========

  async getQQLoginUrl(): Promise<{ authorize_url: string }> {
    return apiService.get<{ authorize_url: string }>('/auth/social/qq/login/');
  },
};
